import axios from 'axios';
import React, { ReactNode, createContext, useContext, useEffect } from 'react';
import { AuthenticatedUserAttributes } from '@loyalty/shared-common';
import { useQuery } from '@tanstack/react-query';
import { isInShell } from '../../shell/shell';
import { useLanguages } from '../../languages/LanguagesContext';

const AuthenticationContext = createContext<AuthStateAttribute>({} as any);

export function useAuth() {
  return useContext(AuthenticationContext);
}

export interface AuthStateAttribute {
  error?: any;
  user?: AuthenticatedUserAttributes;
  isLoading: boolean;
  isLoggedOut: boolean;
}

export async function getUserInfo(): Promise<AuthenticatedUserAttributes> {
  const { data } = await axios.get(`${BASE_PATH}/api/auth/userinfo`, {
    headers: LOCAL_JWT_TOKEN
      ? {
          Authorization: `Bearer ${LOCAL_JWT_TOKEN}`,
        }
      : {},
  });
  return data;
}

export function AuthenticationProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { languages, setCurrentLanguage } = useLanguages();

  const loadUser = useQuery({
    queryKey: ['load-user'],
    queryFn: () => getUserInfo(),
  });

  useEffect(() => {
    const user = loadUser.data;
    if (user && isInShell()) {
      const locale = (user.locale || user.language)?.toLowerCase() || 'en';
      const language = languages.find(language => language.isoCode === locale) || languages[0];
      if (language) {
        // default defined by languageProvider
        setCurrentLanguage(language);
      }
    }
  }, [loadUser.data]);

  return (
    <AuthenticationContext.Provider
      value={{
        error: loadUser.error,
        user: loadUser.data,
        isLoading: loadUser.isPending,
        isLoggedOut: !loadUser.data && !loadUser.isPending,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}
